import React, { useCallback, useEffect } from 'react';
import { useSelector, RootStateOrAny } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { PaginateProductsState } from 'store/ducks/settings/products';

import * as S from './styles';
import { useTranslation, usePermissions } from 'hooks';
import { translations } from './translations';

import { MainContainer, Paginator, MenuSelector } from 'components/shared';
import { GridProducts, FilterProducts } from 'components/settings';
import { useFilters } from 'hooks/useFilters';
import { riskClass } from 'utils';
import {
  ProductReportData,
  useProductReport,
} from './hook/useProductReport';

export const ListProducts: React.FC = () => {
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);
  const { checkUserPermission } = usePermissions();

  const { getData, filters } = useFilters('products');
  const productReportMutation = useProductReport();

  const { data, loading, pagination } = useSelector<RootStateOrAny>(
    (state) => state.paginateProducts
  ) as PaginateProductsState;

  useEffect(() => {
    getData();
  }, [getData]);

  const exportToXLSX = useCallback(
    (products: ProductReportData[]) => {
      if (!products.length) {
        alert(getTranslation('semDadosExportar'));
        return;
      }

      const rows = products.map((product) => [
        product.code ?? '---',
        product.description ?? '---',
        product.business_line
          ? `${product.business_line.activity_division} - ${product.business_line.description}`
          : '---',
        product.product_type?.description ?? '---',
        product.conversion_factor ?? '---',
        product.risk
          ? `${riskClass(product.risk.classes)} - ${product.risk.name}`
          : '---',
        product.risk_number ?? '---',
        product.subsidiary_risk ?? '---',
        product.uno_sap ?? '---',
        product.packing_group ?? '---',
        product.pallet_type?.description ?? '---',
        product.pallet_quantity ?? '---',
      ]);

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([
        [
          getTranslation('codigo'),
          getTranslation('descricao'),
          getTranslation('businessLine'),
          getTranslation('tipoProduto'),
          getTranslation('fatorConversao'),
          getTranslation('classe'),
          getTranslation('numero'),
          getTranslation('riscoSub'),
          getTranslation('numeroOnuSAP'),
          getTranslation('grupo'),
          getTranslation('tipoPalete'),
          getTranslation('quantidadePalete'),
        ],
        ...rows,
      ]);

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Produtos');
      XLSX.writeFile(
        workbook,
        `RELATÓRIO-PRODUTOS-${new Date().getTime()}.xlsx`
      );
    },
    [getTranslation]
  );

  const getFileData = useCallback(() => {
    productReportMutation.mutate(filters || {}, {
      onSuccess: exportToXLSX,
      onError: () => alert(getTranslation('erroExportar')),
    });
  }, [exportToXLSX, filters, getTranslation, productReportMutation]);

  return (
    <MainContainer>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation('configuracoes')} <MenuSelector page="products" />
          {loading && <S.LoadingPage />}
        </h1>
        <S.HeaderButtons>
          <S.ButtonMini
            onClick={getFileData}
            disabled={productReportMutation.isLoading}
			btStyle="primary" 
          >
            {productReportMutation.isLoading ? (
              <S.LoadingButton />
            ) : (
              getTranslation('exportar')
            )}
          </S.ButtonMini>
          {checkUserPermission('insertProduct') && (
            <S.ButtonMini
              onClick={() => history.push('/settings/products/new')}
            >
              {getTranslation('novoProduto')}
            </S.ButtonMini>
          )}
          <FilterProducts />
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <GridProducts products={data} />
        <Paginator
          pagination={pagination}
          settingsPage="products"
          loading={loading}
        />
      </S.PageContent>
    </MainContainer>
  );
};
